import Slider from "react-slick";
import { useState } from "react";
import { toast } from "react-toastify";
import { axios, getErrorMessage, getSdkURL } from "@ultracommerce/react-storefront/global";
import { useSelector } from "react-redux";
import { ProductModifiersModal } from "../../../modules/Product/Controls/ProductModifiersModal";
const LoaderWrapper = ({ loader, children }) => {
  return loader ? <div> Loading .... </div> : children;
};

const ProductFilterGroupList = ({ product, selectedSkus, setSelectedSkus }) => {
  const hasOptionGroupsOnProduct = product?.optionGroups?.length > 0;
  const [selectedOptionGroup, setSelectedOptionGroup] = useState([]);
  const [currentOptionGroup, setCurrentOptionGroup] = useState(hasOptionGroupsOnProduct ? product.optionGroups[0] : {});
  const [optionsToMap, setOptionsToMap] = useState(hasOptionGroupsOnProduct ? product.optionGroups[0].options : []);
  const [showSkuModifiers, setShowSkuModifiers] = useState(null);
  const [loader, setLoader] = useState(false);
  const { addressZoneName } = useSelector((state) => state.userReducer);
  product.modifiers = product?.modifiers || (addressZoneName === "US" ? product?.modifiersUS : product?.modifiersEU);  

  const sliderSettings = {
    dots: false,
    slidesToShow: 2,
    variableWidth: true,
    infinite: false,
  };

  const resetCompStates = () => {
    setOptionsToMap(hasOptionGroupsOnProduct ? product.optionGroups[0].options : []);
    setSelectedOptionGroup([]);
    if (hasOptionGroupsOnProduct) {
      setCurrentOptionGroup({
        optionGroupCode: product.optionGroups[0].optionGroupCode,
        optionGroupName: product.optionGroups[0].optionGroupName,
      });
    } else {
      setCurrentOptionGroup({});
    }
  };

  const getFilteredOptionGroups = (selectedOptionGroupList) => {
    if (!selectedOptionGroupList.length) {
      console.log("Resetting to first level ", selectedOptionGroupList, " selectedOptionGroupList ");
      resetCompStates();
      return;
    }

    //set loader
    setLoader(true);
    setSelectedOptionGroup(selectedOptionGroupList);
    axios({
      method: "POST",
      url: `${getSdkURL()}api/scope/getAvailableOptionGroupsOrSku`,
      data: {
        productID: product.productID,
        selectedOptions: selectedOptionGroupList,
      },
    }).then((response) => {
      setLoader(false);
      if (response?.status === 200 && response?.data?.failureActions.length === 0) {
        if (response?.data?.data?.filteredOptionGroup?.optionGroupCode !== "") {
          setOptionsToMap(response.data.data.filteredOptionGroup.options);
          const exitingFilterGroup = product.optionGroups.find(
            (item) => item.optionGroupCode === response.data.data.filteredOptionGroup.optionGroupCode,
          );
          setCurrentOptionGroup({
            optionGroupCode: exitingFilterGroup.optionGroupCode,
            optionGroupName: exitingFilterGroup.optionGroupName,
          });
        } else {
          if (Object.keys(response?.data?.data?.filteredSku).length) {
            handleSkuAdd(response?.data?.data?.filteredSku);
          } else {
            console.log(" no filter group or sku returned from API, something went wrong. Resetting to first level");
          }

          resetCompStates();
        }
      } else {
        toast.error(getErrorMessage(response?.data?.failureActions));
      }
    });
  };

  const handleOptionAdd = (optionName, optionCode) => {
    const optionGroupExists =
      selectedOptionGroup.filter((item) => {
        return item.optionCode === optionCode && currentOptionGroup.optionGroupCode === item.optionGroupCode;
      }).length > 0;

    if (!optionGroupExists) {
      const selectedOptionGroupList = [
        ...selectedOptionGroup,
        {
          optionName: optionName,
          optionCode: optionCode,
          optionGroupCode: currentOptionGroup.optionGroupCode,
          optionGroupName: currentOptionGroup.optionGroupName,
        },
      ];
      getFilteredOptionGroups(selectedOptionGroupList);
    } else {
      console.log(" This option is already selected, shouldn't happen.");
    }
  };

  const handleSkuAdd = (sku, skuConfiguration, calculatedData, customerMaterialNumber, requestedDeliveryDate, itemCount) => {
    //TODO : make sure all keys are available on sku, since this data comes from 2 different apis
    const transformedSkuRecord = {
      skuCode: sku.skuCode,
      skuID: sku.skuID,
      productName: sku.productName,
      currencyCode: sku?.calculatedData?.modelPriceInfo?.currencyCode,
      price: sku.price,
      skuConfiguration,
      calculatedData,
      customerMaterialNumber,
      requestedDeliveryDate
    };

    let toastMessage = "Items added successfully";

    //check if product is already added
    const productExists = selectedSkus.length > 0 ? selectedSkus?.filter((item) => item.productName === transformedSkuRecord.productName) : [];

    //create new variable to mantain record to be added
    let skuRecordToAdd = {};
    skuRecordToAdd = { productName: transformedSkuRecord.productName, modifiers: product.modifiers };
    if (!productExists.length) {
      //if product doesn't exists, we can append it directly
      skuRecordToAdd["skus"] = [{ ...transformedSkuRecord, quantity: itemCount }];
      setSelectedSkus([...selectedSkus, skuRecordToAdd]);
    } else {
      const finalArrayToAdd = selectedSkus.map((product) => {
        if (product.productName === skuRecordToAdd["productName"]) {
            product.skus.push({ ...transformedSkuRecord, quantity: itemCount });
        }
        return product;
      });
      setSelectedSkus(finalArrayToAdd);
    }

    toast.success(toastMessage);
  };

  const handleOptionRemove = () => {
    if (!selectedOptionGroup.length) {
      console.log("Action calling this method shouldn't be visible ");
      return;
    }
    getFilteredOptionGroups(selectedOptionGroup.slice(0, -1));
  };

  const OptionGroupList = () => {
    return (
      <div className="row mx-1 my-1">
        {hasOptionGroupsOnProduct &&
          optionsToMap.map(({ optionName, optionCode }) => {
            return (
              <button
                key={optionCode}
                className="btn btn-outline-primary trade-order-filter-btn mx-1"
                onClick={(e) => handleOptionAdd(optionName, optionCode)}
              >
                {optionName}
              </button>
            );
          })}
        {!hasOptionGroupsOnProduct && (
          <button
            className="btn btn-outline-primary trade-order-filter-btn mx-1"
            onClick={(e) => {
              if(product.skus.length || product.modifiers.length) {
                setShowSkuModifiers(true);
              } else {
                const defaultSku = product.skus.find((sku) => sku.skuID === product.defaultSku_skuID) || product.skus[0];
                handleSkuAdd({
                  skuCode: defaultSku.skuCode,
                  skuID: defaultSku.skuID,
                  productName: defaultSku.product_productName,
                  price: Math.min(defaultSku.salePrice, defaultSku.listPrice),
                });
              }
            }}
          >
            Add Item
          </button>
        )}
      </div>
    );
  };

  const OptionGroupListWithSlider = () => {
    const optionNamePrefix = selectedOptionGroup
      .map((option) => option.optionGroupName + " " + option.optionName)
      .join(", ");
    return (
      <div className="product-category-slick my-3">
        <button
          className="btn btn-sm btn-danger  mx-1"
          onClick={(e) => {
            handleOptionRemove();
          }}
        >
          Back
        </button>
        <Slider {...sliderSettings}>
          {optionsToMap.map(({ optionName, optionCode }) => {
            return (
              <div key={optionCode}>
                <button
                  className="btn btn-outline-primary btn-sqaure mx-2"
                  onClick={(e) => handleOptionAdd(optionName, optionCode)}
                >
                  {optionNamePrefix + " " + optionName}
                </button>
              </div>
            );
          })}
        </Slider>
      </div>
    );
  };

  return (
    <LoaderWrapper loader={loader}>
      {!selectedOptionGroup.length && <OptionGroupList />}
      {!!selectedOptionGroup.length && <OptionGroupListWithSlider />}
      {showSkuModifiers ? (
        <ProductModifiersModal
          setShow={setShowSkuModifiers}
          product={product}
          addItem={(sku, skuConfiguration,calculatedData, customerMaterialNumber, requestedDeliveryDate, itemCount) => {
            handleSkuAdd(sku, skuConfiguration, calculatedData, customerMaterialNumber, requestedDeliveryDate, itemCount);
            setShowSkuModifiers(false);
          }}
        />
      ) : null}
    </LoaderWrapper>
  );
};

export default ProductFilterGroupList;
